
import { startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, startOfWeek, endOfWeek } from "date-fns";
import { DemoEvent, CategoryFilter } from "@/types/demo-calendar";
import { filterDemoEventsByProximity } from "@/utils/demoEventProximity";
import CalendarHeader from "./CalendarHeader";
import CalendarDaysHeader from "./CalendarDaysHeader";
import CalendarDay from "./CalendarDay";
import TBDEventsSection from "./TBDEventsSection";

interface CalendarGridProps {
  currentDate: Date;
  events: DemoEvent[];
  categoryFilters: CategoryFilter[];
  proximityRadius: number | null;
  userLatitude: number | null;
  userLongitude: number | null;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onGoToToday: () => void;
  onAddEvent: () => void;
  viewMode: 'calendar' | 'list';
  onChangeView: (mode: 'calendar' | 'list') => void;
  onEditEvent: (event: DemoEvent) => void;
  onDeleteEvent: (eventId: string) => void;
  onEventClick: (event: DemoEvent) => void;
  isDeleting?: boolean;
  isAdmin: boolean;
  isLoadingRole: boolean;
}

// Helper function to parse date strings as local dates
const parseLocalDate = (dateStr: string) => {
  // Parse date as local by appending 'T00:00:00' to force local timezone interpretation
  return new Date(dateStr + 'T00:00:00');
};

const CalendarGrid = ({
  currentDate,
  events,
  categoryFilters,
  proximityRadius,
  userLatitude,
  userLongitude,
  onPreviousMonth,
  onNextMonth,
  onGoToToday,
  onAddEvent,
  viewMode,
  onChangeView,
  onEditEvent,
  onDeleteEvent,
  onEventClick,
  isDeleting,
  isAdmin,
  isLoadingRole
}: CalendarGridProps) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  
  // Get the full calendar view - start from Sunday of the week containing the first day
  // and end on Saturday of the week containing the last day
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Filter events based on enabled categories
  const enabledCategories = categoryFilters.filter(f => f.enabled).map(f => f.category);
  const filteredEvents = filterDemoEventsByProximity(
    events.filter(event => enabledCategories.includes(event.gear_category)),
    proximityRadius,
    userLatitude,
    userLongitude,
  );


  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    const dayEvents = filteredEvents.filter(event => {
      if (!event.event_date) return false;
      try {
        // Parse the date as a local date to avoid timezone issues
        const eventDate = parseLocalDate(event.event_date);
        const isSame = isSameDay(eventDate, date);
        return isSame;
      } catch (error) {
        console.error('Error parsing event date:', event.event_date, error);
        return false;
      }
    });
    return dayEvents;
  };

  // Get events without dates (TBD events)
  const tbdEvents = filteredEvents.filter(event => !event.event_date);

  return (
    <div className="bg-card rounded-lg shadow-sm border">
      <CalendarHeader
        currentDate={currentDate}
        onPreviousMonth={onPreviousMonth}
        onNextMonth={onNextMonth}
        onGoToToday={onGoToToday}
        onAddEvent={onAddEvent}
        viewMode={viewMode}
        onChangeView={onChangeView}
        isAdmin={isAdmin}
        isLoadingRole={isLoadingRole}
      />

      <CalendarDaysHeader />

      {/* Calendar Days */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day) => {
          const dayEvents = getEventsForDate(day);

          return (
            <CalendarDay
              key={day.toISOString()}
              day={day}
              currentDate={currentDate}
              dayEvents={dayEvents}
              categoryFilters={categoryFilters}
              onEditEvent={onEditEvent}
              onDeleteEvent={onDeleteEvent}
              onEventClick={onEventClick}
              isDeleting={isDeleting}
              isAdmin={isAdmin}
            />
          );
        })}
      </div>

      <TBDEventsSection
        tbdEvents={tbdEvents}
        categoryFilters={categoryFilters}
        onEditEvent={onEditEvent}
        onDeleteEvent={onDeleteEvent}
        onEventClick={onEventClick}
        isDeleting={isDeleting}
        isAdmin={isAdmin}
      />
    </div>
  );
};

export default CalendarGrid;
